import { serverUrl } from '@utils/MkEnv'
import { createLogger } from '@utils/Logger'
import { API } from '@constants'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { EMkStorageKeys } from '@utils/DataStore'
import { Brand } from '@typescript'
import { transformBrand } from './transformBrand'

// Re-exported so existing `from '@shared/utils/aemApi'` callers (ExperienceFragment, etc.)
// keep working. New low-level callers (e.g. `configs/gtm.ts`) should import from
// `@shared/utils/transformBrand` directly to avoid pulling in this module's session-storage
// cleanup pass and transitive deps for what is otherwise a 6-line pure switch.
export { transformBrand } from './transformBrand'

function buildOptionalAuthHeaders(): Record<string, string> {
  try {
    const session = ProtectedDataStore.getItem<any>(EMkStorageKeys.MAKO_SESSION) || null

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-JWT-Token': '',
      'X-JWT-Member-Token': '',
      'Access-Code': '',
    }

    if (!session) {
      return headers
    }

    const brandId = session?.authBrand ? Brand.getId(session.authBrand) : undefined
    const brandKey = session?.authBrand ? Brand.getKey(session.authBrand) : undefined

    const maybeSet = (k: string, v?: string) => {
      if (v && String(v).trim() !== '') headers[k] = String(v)
    }

    maybeSet('x-brandid', brandId as any)
    maybeSet('x-brandkey', brandKey as any)
    maybeSet('X-JWT-Token', session?.jwtToken)
    maybeSet('X-JWT-Member-Token', session?.memberAccessToken)
    maybeSet('Access-Code', session?.accessCode)
    maybeSet('X-USERID', session?.authorizedUser?.username)

    return headers
  } catch (_) {
    // On any error, return minimal anonymous-safe base headers
    return {
      'Content-Type': 'application/json',
      'X-JWT-Token': '',
      'X-JWT-Member-Token': '',
      'Access-Code': '',
    }
  }
}

// Context-specific loggers
const cacheLogger = createLogger('Cache')
cacheLogger.disableAll()
const aemLogger = createLogger('AEM API')
aemLogger.disableAll()

type ClientlibJson = { css: string[]; js: string[] }

interface CacheEntry<T> {
  data: T
  timestamp: number
  ttl: number
}

class Cache<T> {
  private cache = new Map<string, CacheEntry<T>>()

  private readonly defaultTtl: number

  private readonly useSessionStorage: boolean

  constructor(defaultTtl: number = 5 * 60 * 1000, useSessionStorage: boolean = false) {
    // 5 minutes default
    this.defaultTtl = defaultTtl
    this.useSessionStorage = useSessionStorage
  }

  get(key: string): T | null {
    // Check session storage first for persistent cache
    if (this.useSessionStorage) {
      try {
        const sessionData = sessionStorage.getItem(`aem-cache-${key}`)
        if (sessionData) {
          const entry: CacheEntry<T> = JSON.parse(sessionData)
          const now = Date.now()

          cacheLogger.debug(
            `Checking session storage for key: ${key}, data:`,
            entry.data,
            `type:`,
            typeof entry.data,
            `isNull:`,
            entry.data === null,
            `isUndefined:`,
            entry.data === undefined,
          )

          // Always check for null/undefined data first, regardless of expiration
          if (entry.data === null || entry.data === undefined) {
            cacheLogger.warn(`Found null data in session storage for key: ${key}, removing`)
            sessionStorage.removeItem(`aem-cache-${key}`)
            this.cache.delete(key)
            cacheLogger.debug(`Removed null entry for key: ${key}, returning null`)
            return null
          }

          cacheLogger.debug(`Data is valid for key: ${key}:`, entry.data)

          cacheLogger.debug(`Data is valid for key: ${key}:`, entry.data)

          if (now - entry.timestamp <= entry.ttl) {
            // Also update in-memory cache
            this.cache.set(key, entry)
            cacheLogger.debug(`Returning from session storage for key: ${key}:`, entry.data)
            return entry.data
          }
          // Remove expired entry from session storage
          sessionStorage.removeItem(`aem-cache-${key}`)
        }
      } catch (error) {
        console.warn('Failed to read from session storage:', error)
        // If we can't parse the session data, remove it to prevent future errors
        try {
          sessionStorage.removeItem(`aem-cache-${key}`)
        } catch (removeError) {
          console.warn('Failed to remove corrupted session storage entry:', removeError)
        }
      }
    }

    // Check in-memory cache
    const entry = this.cache.get(key)
    if (!entry) return null

    const now = Date.now()
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key)
      return null
    }

    // Check if the data is null/undefined and clean it up
    if (entry.data === null || entry.data === undefined || entry.data === 'null') {
      cacheLogger.warn(`Found null data in memory cache for key: ${key}, removing`)
      this.cache.delete(key)
      if (this.useSessionStorage) {
        try {
          sessionStorage.removeItem(`aem-cache-${key}`)
        } catch (error) {
          console.warn('Failed to remove null entry from session storage:', error)
        }
      }
      return null
    }

    cacheLogger.debug(`Returning from memory cache for key: ${key}:`, entry.data)
    return entry.data
  }

  set(key: string, data: T, ttl?: number): void {
    // Don't cache null or undefined data
    if (data === null || data === undefined || data === 'null') {
      cacheLogger.warn(`Attempted to cache null/undefined data for key: ${key}`)
      // Remove any existing null entries from both in-memory and session storage
      this.cache.delete(key)
      if (this.useSessionStorage) {
        try {
          sessionStorage.removeItem(`aem-cache-${key}`)
        } catch (error) {
          console.warn('Failed to remove null entry from session storage:', error)
        }
      }
      return
    }

    const entry = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTtl,
    }

    // Update in-memory cache
    this.cache.set(key, entry)

    // Update session storage for persistent cache
    if (this.useSessionStorage) {
      try {
        sessionStorage.setItem(`aem-cache-${key}`, JSON.stringify(entry))
      } catch (error) {
        console.warn('Failed to write to session storage:', error)
      }
    }
  }

  clear(): void {
    this.cache.clear()
    // Clear session storage entries for this cache
    if (this.useSessionStorage) {
      try {
        const keys = Object.keys(sessionStorage)
        keys.forEach((key) => {
          if (key.startsWith('aem-cache-')) {
            sessionStorage.removeItem(key)
          }
        })
      } catch (error) {
        console.warn('Failed to clear session storage:', error)
      }
    }
  }

  size(): number {
    return this.cache.size
  }
}

// Use session storage for header/footer fragments to persist across page reloads
const xfCache = new Cache<string>(5 * 60 * 1000, true) // 5 minutes, with session storage
const clientlibCache = new Cache<ClientlibJson>()

// Clean up any existing null entries in session storage on module load
if (typeof window !== 'undefined' && window.sessionStorage) {
  try {
    const keys = Object.keys(sessionStorage)
    let cleanedCount = 0
    let totalChecked = 0

    cacheLogger.debug(`Checking ${keys.length} session storage keys for null entries...`)

    keys.forEach((key) => {
      if (key.startsWith('aem-cache-')) {
        totalChecked += 1
        try {
          const sessionData = sessionStorage.getItem(key)
          if (sessionData) {
            const entry = JSON.parse(sessionData)
            cacheLogger.debug(`Checking entry: ${key}, data:`, entry.data)
            if (entry.data === null || entry.data === undefined || entry.data === 'null') {
              cacheLogger.warn(`Cleaning up existing null entry: ${key}`)
              sessionStorage.removeItem(key)
              cleanedCount += 1
            }
          }
        } catch (error) {
          console.warn(`[Cache] Failed to parse session storage entry: ${key}, removing corrupted entry`, error)
          sessionStorage.removeItem(key)
          cleanedCount += 1
        }
      }
    })

    cacheLogger.debug(`Checked ${totalChecked} aem-cache keys, cleaned up ${cleanedCount} null/corrupted entries`)
  } catch (error) {
    console.warn('Failed to cleanup existing null entries:', error)
  }
}

// Request deduplication - prevent concurrent identical requests
const pendingRequests = new Map<string, Promise<string>>()

function logApiCall(action: string, brand: string, name: string, cacheKey: string) {
  // Don't log cache hits to reduce console noise
  if (action !== 'CACHE_HIT') {
    aemLogger.debug(`${action}:`, { brand, name, cacheKey, timestamp: new Date().toISOString() })
  }
}

function isNoCacheFragment(name: string): boolean {
  const n = (name || '').toLowerCase()
  return n === 'header'
}

export async function getAemExperienceFragment(brand: string, name: string): Promise<string> {
  const cacheKey = `${brand}::${name}`
  const noCache = isNoCacheFragment(name)

  if (!noCache) {
    // Check cache first
    const cached = xfCache.get(cacheKey)
    aemLogger.debug(`Cache check for ${cacheKey}:`, cached)
    if (cached) {
      logApiCall('CACHE_HIT', brand, name, cacheKey)
      return cached
    }
  }

  aemLogger.debug(`No cache hit for ${cacheKey}, making API call`)

  // Check if there's already a pending request for this key (only when caching is enabled)
  if (!noCache) {
    const pendingRequest = pendingRequests.get(cacheKey)
    if (pendingRequest) {
      logApiCall('PENDING_REQUEST', brand, name, cacheKey)
      return pendingRequest
    }
  }

  logApiCall('API_CALL', brand, name, cacheKey)

  // Create new request
  const requestPromise = (async () => {
    try {
      const params = new URLSearchParams({ brand: transformBrand(brand), name })
      const url = `${serverUrl}${API}/aem/get-xf?${params.toString()}`

      const response = await fetch(url, { credentials: 'include', headers: buildOptionalAuthHeaders() })
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error')
        throw new Error(`Failed to fetch AEM Experience Fragment: ${response.status} - ${errorText}`)
      }
      const json = await response.json()
      const html = json[name]

      if (!html) {
        throw new Error(`No HTML content found in response for fragment: ${name}`)
      }

      // Note: The backend now handles JSON formatting intelligently,
      // so we no longer need to check for double-encoded JSON

      if (!noCache) {
        xfCache.set(cacheKey, html)
      }
      logApiCall('API_SUCCESS', brand, name, cacheKey)
      return html
    } catch (error) {
      logApiCall('API_ERROR', brand, name, cacheKey)
      aemLogger.error(`Error details:`, error)
      aemLogger.error(`Error for ${cacheKey}:`, error instanceof Error ? error.message : String(error))
      throw error
    } finally {
      // Remove from pending requests when done (only for cached path)
      if (!noCache) pendingRequests.delete(cacheKey)
    }
  })()

  // Store the pending request when caching is enabled
  if (!noCache) {
    pendingRequests.set(cacheKey, requestPromise)
  }

  return requestPromise
}

export async function getAemClientlib(brand: string): Promise<ClientlibJson> {
  const mappedBrand = transformBrand(brand)
  const cached = clientlibCache.get(mappedBrand)
  if (cached) {
    return cached
  }
  const params = new URLSearchParams({ brand: mappedBrand })
  const url = `${serverUrl}${API}/aem/get-clientlib?${params.toString()}`
  const response = await fetch(url, { credentials: 'include', headers: buildOptionalAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch AEM Clientlib: ${response.status}`)
  }
  const json = await response.json()

  // Validate that the response contains the expected structure
  if (!json || typeof json !== 'object' || !json.css || !json.js) {
    throw new Error('Invalid clientlib response structure')
  }

  clientlibCache.set(mappedBrand, json)
  return json
}

// Cache management utilities
export const cacheUtils = {
  clearXfCache: () => xfCache.clear(),
  clearClientlibCache: () => clientlibCache.clear(),
  clearAllCaches: () => {
    xfCache.clear()
    clientlibCache.clear()
  },
  getXfCacheSize: () => xfCache.size(),
  getClientlibCacheSize: () => clientlibCache.size(),
  // Clean up null entries from session storage
  cleanupNullEntries: () => {
    try {
      const keys = Object.keys(sessionStorage)
      keys.forEach((key) => {
        if (key.startsWith('aem-cache-')) {
          try {
            const sessionData = sessionStorage.getItem(key)
            if (sessionData) {
              const entry = JSON.parse(sessionData)
              if (entry.data === null || entry.data === undefined || entry.data === 'null') {
                cacheLogger.warn(`Cleaning up null entry: ${key}`)
                sessionStorage.removeItem(key)
              }
            }
          } catch (error) {
            console.warn(`[Cache] Failed to parse session storage entry: ${key}`, error)
            sessionStorage.removeItem(key)
          }
        }
      })
    } catch (error) {
      console.warn('Failed to cleanup null entries:', error)
    }
  },
}

// Synchronous cache check for immediate rendering
export function getCachedExperienceFragment(brand: string, name: string): string | null {
  const cacheKey = `${brand}::${name}`
  return xfCache.get(cacheKey)
}

// Preload function for critical fragments
export function preloadExperienceFragment(brand: string, name: string): Promise<string> {
  const transformedBrand = transformBrand(brand)
  return getAemExperienceFragment(transformedBrand, name)
}

// Preload critical fragments (footer only, header uses own component) on app startup
export function preloadCriticalFragments(brand: string): Promise<void> {
  const transformedBrand = transformBrand(brand)
  const criticalFragments = ['footer']

  return Promise.allSettled(criticalFragments.map((name) => getAemExperienceFragment(transformedBrand, name))).then(() => {
    console.log('Critical fragments preloaded for brand:', brand)
  })
}

// Manual cleanup function that can be called to remove null entries
export function cleanupNullCacheEntries(): void {
  cacheUtils.cleanupNullEntries()
}

// Force cleanup all null entries in session storage
export function forceCleanupNullEntries(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    console.warn('[Cache] Session storage not available')
    return
  }

  try {
    const keys = Object.keys(sessionStorage)
    let cleanedCount = 0
    let totalChecked = 0

    keys.forEach((key) => {
      if (key.startsWith('aem-cache-')) {
        totalChecked += 1
        try {
          const sessionData = sessionStorage.getItem(key)
          if (sessionData) {
            const entry = JSON.parse(sessionData)

            if (entry.data === null || entry.data === undefined || entry.data === 'null') {
              console.warn(`[Cache] Force cleaning up null entry: ${key}`)
              sessionStorage.removeItem(key)
              cleanedCount += 1
            }
          }
        } catch (error) {
          console.warn(`[Cache] Failed to parse session storage entry: ${key}, removing corrupted entry`, error)
          sessionStorage.removeItem(key)
          cleanedCount += 1
        }
      }
    })
  } catch (error) {
    console.warn('Failed to force cleanup null entries:', error)
  }
}
